import { ArrowLeft, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { createObjection } from "../../../api/objectionAPI";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useSWRConfig } from "swr";
import {
  formatDisplayDate,
  getTodayDate,
  toISOForDB,
  validateFutureDate,
} from "../../../utils/Date";
import logger from "../../../utils/logger";
import DateInput from "react-calendar-ui-kit";

interface ObjectionFormData {
  cnr: string;
  caseTitle: string;
  objectionType: string;
  objectionTitle: string;
  description: string;
  legalGround: string;
  nextHearingDate: string;
  priorityLevel: string;
}

const AdvocateObjectForm = ({
  setTab,
  selectedObjection,
  onSubmitEdit,
}: any) => {
  const { mutate } = useSWRConfig();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<ObjectionFormData>({
    defaultValues: {
      cnr: "",
      caseTitle: "",
      objectionType: "",
      objectionTitle: "",
      description: "",
      legalGround: "",
      nextHearingDate: getTodayDate(),
      priorityLevel: "",
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    register("nextHearingDate", {
      required: "Next hearing date is required",
      validate: (val) => {
        if (!val) return "Next hearing date is required";
        const converted = toISOForDB(val);
        if (!converted) return "Must be a valid date";
        return validateFutureDate(converted);
      },
    });
  }, [register]);

  // Effect to populate form when selectedObjection changes
  useEffect(() => {
    if (selectedObjection) {
      setValue("cnr", selectedObjection.cnr || "");
      setValue("caseTitle", selectedObjection.caseTitle || "");
      setValue("objectionType", selectedObjection.objectionType || "");
      setValue("objectionTitle", selectedObjection.objectionTitle || "");
      setValue("description", selectedObjection.description || "");
      setValue("legalGround", selectedObjection.legalGround || "");
      setValue(
        "nextHearingDate",
        selectedObjection.nextHearingDate
          ? formatDisplayDate(selectedObjection.nextHearingDate)
          : "",
      );
      setValue("priorityLevel", selectedObjection.priorityLevel || "");
    }
  }, [selectedObjection, setValue]);

  const onSubmit = async (data: ObjectionFormData) => {
    try {
      setIsSubmitting(true);
      if (data.nextHearingDate) {
        data.nextHearingDate = toISOForDB(data.nextHearingDate) ?? "";
      }

      if (selectedObjection && onSubmitEdit) {
        const payload = { ...selectedObjection, ...data };
        await onSubmitEdit(payload);
        return;
      }

      let res = await createObjection(data);
      let resData = res?.data;
      if (resData?.status) {
        reset();
        toast.success("Objection added successfully");
        await mutate(
          (key: any) =>
            typeof key === "string" &&
            key.startsWith("/api/objection/get-objections"),
          undefined,
          { revalidate: true },
        );
        setTab("list");
      } else {
        toast.error(resData?.message || "Failed to add objection");
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred");
      logger.log("Error adding objection:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          <ArrowLeft
            onClick={() => setTab("list")}
            className="inline-block mr-2 cursor-pointer text-gray-500 hover:text-gray-700"
          />
          {selectedObjection ? "Edit Objection" : "File New Objection"}
        </h2>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
          noValidate
        >
          {/* Case Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CNR Number *
              </label>
              <input
                type="text"
                maxLength={16}
                {...register("cnr", {
                  required: "Cnr number is required",
                  minLength: {
                    value: 1,
                    message: "CNR must be at least 1 character",
                  },
                  maxLength: {
                    value: 16,
                    message: "CNR must not exceed 16 characters",
                  },
                })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="e.g. DLCT011234598765"
              />
              {errors.cnr && (
                <p className="text-red-500 text-sm">{errors.cnr.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Case Title *
              </label>
              <input
                type="text"
                {...register("caseTitle", {
                  required: "Case title is required",
                  // pattern: {
                  //   value: /^[A-Za-z\s]+$/, // only letters and spaces
                  //   message: "Only alphabets are allowed",
                  // },
                  minLength: {
                    value: 2,
                    message: "Case title must be at least 2 characters",
                  },
                })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Brief case title"
              />
              {errors.caseTitle && (
                <p className="text-red-500 text-sm">
                  {errors.caseTitle.message}
                </p>
              )}
            </div>
          </div>

          {/* Objection Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objection Type *
              </label>
              <select
                {...register("objectionType", {
                  required: "Objection type is required",
                })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              >
                <option value="" disabled>
                  Select Objection
                </option>
                <option value="legal">Legal Objection</option>
                <option value="procedural">Procedural Objection</option>
                <option value="evidence">Evidence Objection</option>
                <option value="jurisdiction">Jurisdiction Objection</option>
                <option value="other">Other</option>
              </select>
              {errors.objectionType && (
                <p className="text-red-500 text-sm">
                  {errors.objectionType.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority Level *
              </label>
              <select
                {...register("priorityLevel", {
                  required: "Priority is required",
                })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              >
                <option value="" disabled>
                  Select Priority
                </option>
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent</option>
              </select>
              {errors.priorityLevel && (
                <p className="text-red-500 text-sm">
                  {errors.priorityLevel.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Objection Title *
            </label>
            <input
              type="text"
              {...register("objectionTitle", {
                required: "Objection title is required",
                // pattern: {
                //   value: /^[A-Za-z\s]+$/, // only letters and spaces
                //   message: "Only alphabets are allowed",
                // },
                minLength: {
                  value: 2,
                  message: "Case title must be at least 2 characters",
                },
              })}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Brief title describing the objection"
            />
            {errors.objectionTitle && (
              <p className="text-red-500 text-sm">
                {errors.objectionTitle.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Objection Description *
            </label>
            <textarea
              {...register("description", {
                required: "Description is required",
                // pattern: {
                //   value: /^[A-Za-z\s]+$/, // only letters and spaces
                //   message: "Only alphabets are allowed",
                // },
                minLength: {
                  value: 5,
                  message: "Description must be at least 5 characters",
                },
              })}
              rows={4}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Detailed description of the objection..."
            />
            {errors.description && (
              <p className="text-red-500 text-sm">
                {errors.description.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Legal Grounds *
            </label>
            <textarea
              {...register("legalGround", {
                required: "Legal grounds are required",
                // pattern: {
                //   value: /^[A-Za-z\s]+$/, // only letters and spaces
                //   message: "Only alphabets are allowed",
                // },
              })}
              rows={3}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Legal basis and grounds for the objection..."
            />
            {errors.legalGround && (
              <p className="text-red-500 text-sm">
                {errors.legalGround.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hearing Date *
            </label>
            <DateInput
              value={watch("nextHearingDate") || getTodayDate()} // 👈 fallback do
              onChange={(val) => {
                setValue("nextHearingDate", val);
                trigger("nextHearingDate");
              }}
              theme={{
                yearButtonWidth: "140px",
                selectWidth: "120px",
                inputPadding: "13px 14px",
              }}
            />

            {errors.nextHearingDate && (
              <p className="text-red-500 text-sm mt-1">
                {errors.nextHearingDate.message as string}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-4">
            {/* {!selectedObjection && (
              <button
                type="button"
                onClick={() => reset()}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset Form
              </button>
            )} */}
            {!selectedObjection && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 disabled:opacity-70"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting ? "Filing..." : "File Objection"}
              </button>
            )}
            {/* File Objection button */}

            {/* Save Changes button */}
            {selectedObjection && (
              <>
                <button
                  type="button"
                  onClick={() => setTab("list")}
                  className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2 disabled:opacity-70"
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvocateObjectForm;
